import React from "react";
import { type NextPage } from "next";
import { Page } from "components/page";
import { type CdpIndustrySlug, cdpIndustryVariants } from "contexts/cdp-industry-context";
import {
  cardHoverTranslation,
  getLandingPageProps,
  getPageMetaImage,
  capitalizeFirstLetter,
  type LandingPageProps,
} from "utils";
import { Section, sectionSpacingY } from "components/section";
import { Link } from "ui/link";
import { Box, Flex, SimpleGrid, Text } from "@chakra-ui/react";
import { IconChip } from "ui/chips";
import { BasicGradientHero } from "components/hero";
import { BookDemoButton } from "components/event-tracking";

const CdpIndustryHub: NextPage<LandingPageProps> = ({ announcementBanner, page }) => {
  const slugs: string[] = Object.keys(cdpIndustryVariants);

  return (
    <Page
      announcementBanner={announcementBanner}
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      bgColor="new.green.50"
    >
      <BasicGradientHero
        variant="green"
        shade="50"
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        maxW="5xl"
        descriptionMaxW="3xl"
        actions={<BookDemoButton />}
        pt={{ base: "bigger", md: "huge" }}
      />

      <Section my="0" mt="small" pb={sectionSpacingY}>
        <Flex justifyContent="center">
          <SimpleGrid
            as="ul"
            columns={{ base: 1, md: 2, lg: 3 }}
            w="100%"
            gap="small"
            pb="biggest"
            listStyleType="none"
            justifyItems="center"
          >
            {slugs.map((slug) => {
              const industry = cdpIndustryVariants[slug as CdpIndustrySlug];
              return (
                /**
                 * Parent element is necessary to avoid a layout shift when hovering over the bottom of the card.
                 * See `cardHoverTranslation` for more details.
                 */
                <Box key={slug} className="group" as="li" w="100%" maxW="cardMaxW">
                  <Link
                    variant="unstyled"
                    href={`/cdp-industries/${slug}`}
                    boxSize="100%"
                    borderRadius="base"
                    _groupHover={cardHoverTranslation}
                  >
                    <Flex
                      pos="relative"
                      boxSize="100%"
                      align="center"
                      gap="smaller"
                      p="small"
                      bg="brand.background.light"
                      borderRadius="base"
                      boxShadow="card"
                      border="1px"
                      borderColor="brand.border.extraLight"
                    >
                      <IconChip variant={industry.color} icon={industry.icon} />
                      <Text as="span" textStyle="body3" fontWeight="semibold" sx={capitalizeFirstLetter}>
                        {industry.name}
                      </Text>
                    </Flex>
                  </Link>
                </Box>
              );
            })}
          </SimpleGrid>
        </Flex>
      </Section>
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/cdp-industries");

export default CdpIndustryHub;
